import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import ApiConfig from "../../api/ApiConfig";
import {
  PostWithAuth,
  postWithAuthCallWithErrorResponse,
  simpleGetCallWithErrorResponse,
} from "../../api/ApiServices";
import { AppContext } from "../../context/AppContext";
import { notifyError, notifySuccess } from "../../sharedComponent/notify";
import VariationModal from "../../utils/variationModal/VariationModal";
import { getAddonDataArr } from "../../utils/AddonService";
import veg_icon from "../../assets/images/icons/veg_icon.svg";
import nonveg_icon from "../../assets/images/icons/nonveg_icon.svg";
import plusIco from "../../assets/images/plusIcon.svg";
import minusIco from "../../assets/images/minusIcon.svg";
import dr_2 from "../../assets/images/biry.jpg";

const HOME_MENU_LIMIT = 12;

// ── Helpers ───────────────────────────────────────────────────────────────────
const getDisplayPrice = (item) => {
  const base      = parseFloat(item?.item_price ?? 0);
  const variation = parseFloat(item?.variations?.[0]?.variation_price ?? 0);
  return item?.item_allow_variation === 1 ? base + variation : base;
};

const isOutOfStock = (item) =>
  item?.item_active === 0 ||
  item?.in_stock    === 0 ||
  (item?.in_stock === 1 && item?.quantity === 0) ||
  (item?.item_allow_variation === 1 && item?.in_stock === 1 && item?.variations?.length === 0);

// Builds the GET_MENU query string from current filters + pagination
const buildUrl = (pageNum, store, filters, limit = HOME_MENU_LIMIT) => {
  const p = new URLSearchParams({
    restaurant_id: store,
    page_limit:    limit,
    page:          pageNum,
  });
  if (filters.categoryId) p.set("category", filters.categoryId);

  // Dietary  →  attribute=Veg | Non-Veg
  if (filters.dietaryFilter === "veg")     p.set("attribute", "Veg");
  if (filters.dietaryFilter === "non-veg") p.set("attribute", "Non-Veg");

   if (filters.dietaryFilter === "Recommended") p.set("type", "Recommended");
   if (filters.dietaryFilter === "Popular") p.set("type", "Popular");

  // Price sort  →  price=Low-to-High | High-to-Low
  if (filters.activeSort === "price_asc")  p.set("price", "Low-to-High");
  if (filters.activeSort === "price_desc") p.set("price", "High-to-Low");

  // Available  →  in_stock=1
  if (filters.activeSort === "available")  p.set("in_stock", "1");

  // Takeaway / Ready-to-use toggles
  if (filters.takeaway)    p.set("order_type",   "takeaway");
  if (filters.readyToUse)  p.set("ready_to_use", "true");

  // Search text
  if (filters.search)      p.set("search", filters.search);

  return `${ApiConfig.GET_MENU}?${p.toString()}`;
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const SkeletonCards = () =>
  Array(HOME_MENU_LIMIT).fill(0).map((_, i) => (
    <div key={i} className="mc-item-card mc-skeleton-card">
      <div className="mc-card-image-wrap mc-skeleton-img" />
      <div className="mc-card-body">
        <div className="mc-skeleton-line" style={{ width: "72%" }} />
        <div className="mc-skeleton-line" style={{ width: "44%", marginTop: "8px" }} />
        <div className="mc-skeleton-line" style={{ width: "60%", marginTop: "16px", height: "32px", borderRadius: "8px" }} />
      </div>
    </div>
  ));

// ── Component ─────────────────────────────────────────────────────────────────
// Props:
//   categoryId    — API category filter (null = all)
//   dietaryFilter — "all" | "veg" | "non-veg"
//   activeSort    — null | "price_asc" | "price_desc" | "available"
//   onCartChange  — callback after any cart mutation (parent can refresh cart count)
//   showEmptyState — render empty-state UI instead of returning null when list is empty
//   emptyAction   — optional JSX node rendered inside the empty state (e.g. Clear Filters btn)
const HomeMenuSection = ({
  categoryId    = null,
  dietaryFilter = "all",
  activeSort    = null,
  takeaway      = false,
  readyToUse    = false,
  search        = "",
  onCartChange  = null,
  showEmptyState = false,
  emptyAction   = null,
}) => {
  const { selectedStore, defaultImgs, subscribeToEvent } = useContext(AppContext);

  const [menuList,          setMenuList]          = useState([]);
  const [isLoading,         setIsLoading]         = useState(true);
  const [isFetchingMore,    setIsFetchingMore]    = useState(false);
  const [showVariationModal,setShowVariationModal] = useState(false);
  const [variationData,     setVariationData]     = useState(null);

  const pageRef     = useRef(1);
  const sentinelRef = useRef(null);
  // Refs so stable callbacks always read current values without re-creating
  const storeRef    = useRef(selectedStore);
  const filtersRef  = useRef({ categoryId, dietaryFilter, activeSort });
  const hasMoreRef  = useRef(true);
  const fetchingRef = useRef(false);

  const defaultImg = defaultImgs?.item_image || dr_2;

  // Keep refs in sync on every render
  useEffect(() => { storeRef.current = selectedStore; }, [selectedStore]);
  useEffect(() => {
    filtersRef.current = { categoryId, dietaryFilter, activeSort, takeaway, readyToUse, search };
  }, [categoryId, dietaryFilter, activeSort, takeaway, readyToUse, search]);

  // ── Load more (stable — all mutable state via refs) ────────────────────────
  const loadMore = useCallback(async () => {
    if (!storeRef.current || fetchingRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    fetchingRef.current = true;
    setIsFetchingMore(true);
    try {
      const url = buildUrl(nextPage, storeRef.current, filtersRef.current);
      const res = await simpleGetCallWithErrorResponse(url);
      if (res?.json?.success) {
        const data = res.json.data || [];
        if (data.length > 0) {
          setMenuList(prev => [...prev, ...data]);
          pageRef.current = nextPage;
        }
        hasMoreRef.current = !res.json.last_page;
      } else {
        hasMoreRef.current = false;
      }
    } catch (_) {
      hasMoreRef.current = false;
    } finally {
      fetchingRef.current = false;
      setIsFetchingMore(false);
    }
  }, []); // stable — never recreated

  // ── Refresh after cart mutation (stable) ───────────────────────────────────
  const refreshMenu = useCallback(async () => {
    const store   = storeRef.current;
    const filters = filtersRef.current;
    if (!store) return;
    try {
      const url = buildUrl(1, store, filters, pageRef.current * HOME_MENU_LIMIT);
      const res = await simpleGetCallWithErrorResponse(url);
      if (res?.json?.success) {
        setMenuList(res.json.data || []);
        hasMoreRef.current = !res.json.last_page;
      }
    } catch (_) {}
  }, []); // stable

  // ── Reset + reload when store or any filter changes ────────────────────────
  useEffect(() => {
    if (!selectedStore) return;
    pageRef.current   = 1;
    hasMoreRef.current  = true;
    fetchingRef.current = false;
    setMenuList([]);
    setIsFetchingMore(false);
    setIsLoading(true);

    let cancelled = false;
    const url = buildUrl(1, selectedStore, { categoryId, dietaryFilter, activeSort, takeaway, readyToUse, search });
    simpleGetCallWithErrorResponse(url)
      .then(res => {
        if (cancelled) return;
        if (res?.json?.success) {
          setMenuList(res.json.data || []);
          hasMoreRef.current = !res.json.last_page;
        } else {
          hasMoreRef.current = false;
        }
      })
      .catch(() => { if (!cancelled) hasMoreRef.current = false; })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, [selectedStore, categoryId, dietaryFilter, activeSort, takeaway, readyToUse, search]);

  // ── Real-time menu refresh via socket ──────────────────────────────────────
  useEffect(() => {
    if (!selectedStore || !subscribeToEvent) return;
    const delay = 1500 + Math.random() * 1000;
    let tid;
    const unsub = subscribeToEvent(`${selectedStore}/updated-menu`, () => {
      tid = setTimeout(() => refreshMenu(), delay);
    });
    return () => { clearTimeout(tid); unsub?.(); };
  }, [selectedStore, refreshMenu]);

  // ── Intersection observer — set up once, sentinel always in DOM ────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []); // stable — loadMore never changes

  // ── Cart actions ───────────────────────────────────────────────────────────
  const handleAddToCart = (item) => {
    if (item.item_allow_variation == 1 || item.item_allow_addon) {
      setVariationData(item);
      setShowVariationModal(true);
      return;
    }
    addToCart(item);
  };

  const addToCart = async (item, variationId = null) => {
    try {
      const loginData = JSON.parse(localStorage.getItem("LOGIN_DATA"));
      const res = await postWithAuthCallWithErrorResponse(
        ApiConfig.Add_TO_CART,
        JSON.stringify({
          item_id:        item.item_id,
          variation_id:   item.item_allow_variation == 1 ? variationId : null,
          user_id:        loginData.id,
          order_quantity: 1,
          addons:         getAddonDataArr(item),
        })
      );
      if (res?.json?.success) {
        notifySuccess(res.json.message);
        refreshMenu();
        onCartChange?.();
      }
    } catch (e) { notifyError(e?.json?.message); }
  };

  const changeQuantity = async (item, increment) => {
    try {
      const addons = (item?.cart_addons || []).map(a => ({
        cart_addon_id: a.addon_cart_id,
        quantity:      1,
        addon_id:      a.addon_id,
      }));
      const res = await PostWithAuth(ApiConfig.UPDATE_CART, {
        cart_id:        item.cart_id,
        order_quantity: increment ? item.cart_quantity + 1 : item.cart_quantity - 1,
        addons,
      });
      if (res?.success) {
        notifySuccess(res.message);
        refreshMenu();
        onCartChange?.();
      }
    } catch (e) { notifyError(e?.json?.message); }
  };

  const deleteCartItem = async (item) => {
    try {
      const res = await PostWithAuth(ApiConfig.REMOVE_CART_ITEM, { cart_id: item.cart_id });
      if (res?.success || res?.json?.success) {
        notifySuccess(res.message || res.json.message);
        refreshMenu();
        onCartChange?.();
      }
    } catch (e) { notifyError(e?.json?.message); }
  };

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!isLoading && menuList.length === 0) {
    if (!showEmptyState) return null;
    return (
      <div className="mc-empty-state">
        <div className="mc-empty-icon">🍽️</div>
        <p className="mc-empty-title">No items found</p>
        <p className="mc-empty-sub">Try adjusting your filters or selecting a different category.</p>
        {emptyAction}
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="home-menu-section">
      <div className="mc-items-grid">
        {isLoading ? (
          <SkeletonCards />
        ) : (
          menuList.map(item => {
            const oos       = isOutOfStock(item);
            const price     = getDisplayPrice(item);
            const cart_data = item?.cart?.find(c => c?.cart_item_id == item?.item_id);

            return (
              <div key={item.item_id} className={`mc-item-card${oos ? " greyscale" : ""}`}>
                <div className="mc-card-image-wrap">
                  {item.is_ready_to_use && (
                    <div className="ready-to-use-badge">
                      <span className="badge-icon">✦</span> Ready to Eat
                    </div>
                  )}
                  <img
                    src={item.item_image_url || defaultImg}
                    alt={item.item_name}
                    onError={e => { e.target.onerror = null; e.target.src = defaultImg; }}
                  />
                  {oos && (
                    <div className="out-of-stock-overlay">
                      <span className="out-of-stock-text">Out of Stock</span>
                    </div>
                  )}
                </div>

                <div className="mc-card-body">
                  <p className="mc-card-name">{item.item_name}</p>

                  <div className="mc-card-meta">
                    <span className="mc-card-price">₹{price}</span>
                    {(item.attribute_name === "veg" || item.attribute_name === "non-veg") && (
                      <img
                        width={20} height={20}
                        src={item.attribute_name === "veg" ? veg_icon : nonveg_icon}
                        alt={item.attribute_name}
                      />
                    )}
                  </div>

                  {(item.item_allow_variation == 1 || item.item_allow_addon) ? (
                    <p className="mc-customizable-tag">Customization available</p>
                  ) : null}

                  <div className="mc-card-actions">
                    {cart_data?.cart_quantity > 0 ? (
                      <div className="d-flex justify-content-center">
                        <div className="button-main qntyBox-button">
                          <label
                            onClick={() =>
                              cart_data.cart_quantity === 1
                                ? deleteCartItem(cart_data)
                                : changeQuantity(cart_data, false)
                            }
                            style={{ cursor: "pointer" }}
                          >
                            <img src={minusIco} alt="-" />
                          </label>
                          <label className="value">{cart_data.cart_quantity}</label>
                          <label
                            onClick={() => changeQuantity(cart_data, true)}
                            style={{ cursor: "pointer" }}
                          >
                            <img src={plusIco} alt="+" />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-center">
                        <button
                          className="button-main"
                          onClick={() => handleAddToCart(item)}
                          disabled={oos}
                        >
                          Add to cart
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Sentinel — always in DOM */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {isFetchingMore && (
        <div className="mc-items-grid">
          <SkeletonCards />
        </div>
      )}

      <VariationModal
        showModal={showVariationModal}
        item={variationData}
        onClose={() => setShowVariationModal(false)}
        onAdd={(customizedItem, variationId) => {
          addToCart(customizedItem, variationId);
          setShowVariationModal(false);
        }}
      />
    </div>
  );
};

export default HomeMenuSection;
